import {
    IconAlertCircle,
    IconCheck,
    IconChevronLeft,
    IconChevronRight,
    IconHistory,
    IconPencil
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { debug } from '../utils/debug';
import { ConfirmationDialog } from './ui/confirmation-dialog';

/**
 * QuickTrainMerged Component
 * Combines Time Machine carousel with Quick Train form
 * Browse training history and populate form from selected examples
 */
export default function QuickTrainMerged({ locationId }) {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    spam: 0,
    legitimate: 0,
    spamRatio: 0,
  });

  // Quick Trainer state - form fields
  const [trainer, setTrainer] = useState({
    senderEmail: '',
    senderName: '',
    subject: '',
    bodyPreview: '',
    isSpam: true,
    confidence: 0.9,
    submitting: false,
    submittedOk: false,
  });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Alert state for styled alerts
  const [alert, setAlert] = useState(null);

  const showAlert = (message, type = 'error') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const fetchTrainingExamples = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/spam-rules/${encodeURIComponent(locationId)}/training-examples`
      );
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

      const data = await res.json();
      const examplesList = data.examples || data || [];

      setExamples(examplesList);
      setCurrentIndex(0);

      // Calculate stats
      const total = examplesList.length;
      const spam = examplesList.filter(e => e.is_spam).length;
      const legitimate = total - spam;
      const spamRatio = total > 0 ? ((spam / total) * 100).toFixed(1) : 0;

      setStats({ total, spam, legitimate, spamRatio });
    } catch (err) {
      debug.error('[QuickTrainMerged] Failed to fetch examples:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!locationId) return;
    fetchTrainingExamples();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const currentExample = examples[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? examples.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev === examples.length - 1 ? 0 : prev + 1));
  };

  const goToIndex = (idx) => {
    setCurrentIndex(Math.max(0, Math.min(idx, examples.length - 1)));
  };

  // Select example and populate form
  const selectExample = (example) => {
    setTrainer({
      senderEmail: example.sender_email || '',
      senderName: example.sender_name || '',
      subject: example.subject || '',
      bodyPreview: example.body_preview || '',
      isSpam: example.is_spam || false,
      confidence: example.confidence ?? 0.9,
      submitting: false,
      submittedOk: false,
    });
  };

  // Initiate delete - show confirmation dialog
  const initiateDelete = (exampleId) => {
    setDeleteConfirm(exampleId);
    setShowDeleteDialog(true);
  };

  // Delete training example
  const handleDeleteExample = async () => {
    const exampleId = deleteConfirm;
    setShowDeleteDialog(false);

    try {
      const res = await fetch(
        `/api/spam-rules/${encodeURIComponent(locationId)}/training-example/${exampleId}`,
        { method: 'DELETE' }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete example');
      }

      setDeleteConfirm(null);
      // Refresh examples list
      fetchTrainingExamples();
    } catch (e) {
      showAlert(e.message || 'Failed to delete example', 'error');
      setDeleteConfirm(null);
    }
  };

  // Submit training example
  const handleSubmit = async () => {
    if (!trainer.subject && !trainer.bodyPreview && !trainer.senderEmail) {
      showAlert('Add at least a subject, snippet, or sender.', 'warning');
      return;
    }

    setTrainer(t => ({ ...t, submitting: true, submittedOk: false }));
    try {
      const res = await fetch(`/api/spam-rules/${encodeURIComponent(locationId)}/training-example`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: '',
          sender_email: trainer.senderEmail || '',
          sender_name: trainer.senderName || '',
          subject: trainer.subject || '',
          body_preview: trainer.bodyPreview || '',
          is_spam: !!trainer.isSpam,
          confidence: trainer.confidence ?? null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit training example');
      }

      setTrainer(t => ({
        ...t,
        submittedOk: true,
        senderEmail: '',
        senderName: '',
        subject: '',
        bodyPreview: ''
      }));

      // Refresh examples list
      setTimeout(() => {
        fetchTrainingExamples();
        setTrainer(t => ({ ...t, submittedOk: false }));
      }, 1500);
    } catch (e) {
      showAlert(e.message || 'Failed to submit', 'error');
    } finally {
      setTrainer(t => ({ ...t, submitting: false }));
    }
  };

  if (loading && examples.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin">⏳</div>
          <span>Loading training examples...</span>
        </div>
      </div>
    );
  }

  if (error && examples.length === 0) {
    return (
      <div className="bg-red-500/20 rounded-lg p-4 border border-red-500">
        <p className="text-red-200">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Styled Alert */}
      {alert && (
        <div className={`rounded-lg p-4 border transition-all ${
          alert.type === 'error'
            ? 'bg-red-500/20 border-red-500 text-red-200'
            : alert.type === 'warning'
            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-200'
            : 'bg-green-500/20 border-green-500 text-green-200'
        }`}>
          <p className="font-medium">{alert.message}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs opacity-80">Total Examples</div>
        </div>
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.spam}</div>
          <div className="text-xs opacity-80">Spam</div>
        </div>
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.legitimate}</div>
          <div className="text-xs opacity-80">Legitimate</div>
        </div>
        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.spamRatio}%</div>
          <div className="text-xs opacity-80">Spam Ratio</div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left Column: Time Machine Carousel */}
        {examples.length > 0 && (
          <div>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <IconHistory size={24} />
              Training History
            </h3>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden">

              {/* Main Carousel with Time Machine Tunnel Effect */}
              <div className="relative overflow-hidden" style={{ perspective: '1000px' }}>
                {/* Background tunnel effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 via-purple-900/20 to-black pointer-events-none" />

                {/* 3D Tunnel - Previous examples receding */}
                <div className="relative h-72 flex items-center justify-center" style={{ transformStyle: 'preserve-3d' }}>
                  {/* Receding examples (past) */}
                  {examples.map((ex, idx) => {
                    const relativeIdx = idx - currentIndex;
                    const isPast = relativeIdx < 0;
                    const isPresent = relativeIdx === 0;
                    const isFuture = relativeIdx > 0;

                    if (Math.abs(relativeIdx) > 3) return null; // Only show 3 items back/forward

                    const depth = Math.abs(relativeIdx);
                    const zDepth = isPast ? -depth * 150 : isFuture ? -depth * 150 : 0;
                    const scale = Math.pow(0.75, depth);
                    const opacity = 1 - depth * 0.25;

                    return (
                      <div
                        key={idx}
                        className={`absolute transition-all duration-300 rounded-lg border ${
                          isPresent
                            ? 'border-blue-500 z-40 cursor-pointer'
                            : 'border-white/20 cursor-pointer hover:border-white/40'
                        } ${isPast ? 'opacity-40' : ''}`}
                        style={{
                          transform: `translateZ(${zDepth}px) scale(${scale})`,
                          opacity: isPresent ? 1 : opacity,
                          pointerEvents: 'auto',
                          width: 'min(90%, 280px)',
                        }}
                        onClick={() => {
                          goToIndex(idx);
                          selectExample(ex);
                        }}
                      >
                        <div className={`p-3 rounded-lg ${
                          isPresent ? 'bg-gray-800/70 backdrop-blur-sm' : 'bg-gray-900/50'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${
                              ex.is_spam
                                ? 'bg-red-500/30 text-red-200'
                                : 'bg-green-500/30 text-green-200'
                            }`}>
                              {ex.is_spam ? (
                                <IconAlertCircle size={14} />
                              ) : (
                                <IconCheck size={14} />
                              )}
                            </span>
                            {ex.confidence && (
                              <span className="text-xs opacity-70">{(ex.confidence * 100).toFixed(0)}%</span>
                            )}
                          </div>
                          {isPresent && (
                            <p className="text-xs opacity-80 line-clamp-2">{ex.subject || ex.sender_email || ex.sender_name}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation arrows */}
                <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
                  <button
                    onClick={goToPrevious}
                    className="p-2 rounded-lg bg-gray-700/80 hover:bg-gray-600 text-white font-semibold pointer-events-auto transition-colors"
                    title="Previous"
                  >
                    <IconChevronLeft size={24} />
                  </button>
                  <button
                    onClick={goToNext}
                    className="p-2 rounded-lg bg-gray-700/80 hover:bg-gray-600 text-white font-semibold pointer-events-auto transition-colors"
                    title="Next"
                  >
                    <IconChevronRight size={24} />
                  </button>
                </div>

                {/* Progress indicator */}
                <div className="absolute bottom-3 left-4 right-4 text-center text-xs opacity-70">
                  {currentIndex + 1} / {examples.length}
                </div>
              </div>

              {/* Current Example Preview */}
              {currentExample && (
                <div className="bg-gray-800/50 border-t border-white/10 p-4">
                  <div className="space-y-2">
                    {currentExample.sender_name && (
                      <div>
                        <label className="text-xs font-semibold opacity-70 uppercase">From</label>
                        <p className="text-sm">{currentExample.sender_name}</p>
                      </div>
                    )}
                    {currentExample.sender_email && (
                      <div>
                        <label className="text-xs font-semibold opacity-70 uppercase">Email</label>
                        <p className="text-xs font-mono opacity-80">{currentExample.sender_email}</p>
                      </div>
                    )}
                    {currentExample.subject && (
                      <div>
                        <label className="text-xs font-semibold opacity-70 uppercase">Subject</label>
                        <p className="text-sm line-clamp-2">{currentExample.subject}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => selectExample(currentExample)}
                      className="flex-1 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors"
                    >
                      Use This Example ↓
                    </button>
                    <button
                      onClick={() => initiateDelete(currentExample.id)}
                      className="px-3 py-2 rounded-lg font-semibold text-sm transition-colors bg-red-600/40 hover:bg-red-600 text-red-200 hover:text-white"
                      title="Delete this training example"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Column: Quick Train Form */}
        <div>
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <IconPencil size={24} />
            Add or Edit Example
          </h3>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 space-y-4">
            <p className="text-sm opacity-80">
              {examples.length > 0
                ? 'Select an example from the left, or fill in details below to add a new one.'
                : 'Fill in the fields below to add your first training example.'}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block mb-1 font-medium text-sm">Sender Email (optional)</label>
                <input
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-white/20"
                  placeholder="spammer@example.com"
                  value={trainer.senderEmail}
                  onChange={(e) => setTrainer(t => ({ ...t, senderEmail: e.target.value }))}
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-sm">Sender Name (optional)</label>
                <input
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-white/20"
                  placeholder="DocuSign Support"
                  value={trainer.senderName}
                  onChange={(e) => setTrainer(t => ({ ...t, senderName: e.target.value }))}
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-sm">Subject (optional)</label>
                <input
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-white/20"
                  placeholder="Please review and sign your document"
                  value={trainer.subject}
                  onChange={(e) => setTrainer(t => ({ ...t, subject: e.target.value }))}
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-sm">Snippet / Key Phrases</label>
                <textarea
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-white/20 min-h-[120px]"
                  placeholder="Paste suspicious lines, links, or phrases here..."
                  value={trainer.bodyPreview}
                  onChange={(e) => setTrainer(t => ({ ...t, bodyPreview: e.target.value }))}
                />
                <p className="text-xs opacity-70 mt-1">Tip: Include exact phrases you want caught in future emails.</p>
              </div>
            </div>

            {/* Settings Row */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-white/20">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={trainer.isSpam}
                  onChange={(e) => setTrainer(t => ({ ...t, isSpam: e.target.checked }))}
                />
                <span className="text-sm">Mark as spam</span>
              </label>

              <div className="flex items-center gap-3 sm:ml-auto">
                <span className="text-sm opacity-80">Confidence:</span>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  className="w-20 bg-gray-700 text-white px-2 py-1 rounded border border-white/20 text-sm"
                  value={trainer.confidence}
                  onChange={(e) => setTrainer(t => ({ ...t, confidence: parseFloat(e.target.value || '0') }))}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors ${
                trainer.submitting
                  ? 'bg-gray-600 cursor-not-allowed'
                  : trainer.submittedOk
                  ? 'bg-green-600'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
              disabled={trainer.submitting}
              onClick={handleSubmit}
            >
              {trainer.submitting && '⏳ Saving...'}
              {!trainer.submitting && trainer.submittedOk && '✅ Saved!'}
              {!trainer.submitting && !trainer.submittedOk && '💾 Add Training Example'}
            </button>
          </div>
        </div>
      </div>

      {/* No Examples State */}
      {examples.length === 0 && (
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20 text-center">
          <h3 className="text-xl font-bold mb-2">No Training History Yet</h3>
          <p className="opacity-80 mb-6">
            Add your first training example using the form to get started. The AI will learn from these examples.
          </p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteConfirm(null);
        }}
        onConfirm={handleDeleteExample}
        title="Delete Training Example"
        description="Are you sure you want to delete this training example? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
